// React
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { NumericFormat } from "react-number-format";

// MUI
import {
    Box,
    TextField,
    Grid,
    Divider,
    Card,
    CardHeader,
    CardContent,
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    OutlinedInput,
    InputAdornment,
    Select,
    MenuItem,
    Switch,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

/** MUI icons */
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const PREFIX = "KiiperTaxes";

const classes = {
    btnRoot: `${PREFIX}-btnRoot`,
    buttonContainer: `${PREFIX}-buttonContainer`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.btnRoot}`]: {
        border: "1px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "#2f3190",
            border: "1px solid #2f3190",
        },
    },
    [`& .${classes.buttonContainer}`]: {
        display: "flex",
        justifyContent: "flex-end",
        padding: 15,
    },
}));

const NumberFormatCustom = (props) => {
    const todos = useSelector((state) => state.todos);

    const { inputRef, value, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            value={value}
            getInputRef={inputRef}
            onValueChange={({ value: v }) => onChange({ target: { value: v } })}
            thousandSeparator={todos.amountFormat === "de-DE" ? "." : ","}
            decimalSeparator={todos.amountFormat === "de-DE" ? "," : "."}
            fixedDecimalScale={true}
            decimalScale={2}
            valueIsNumericString
        />
    );
};

const defaultState = {
    fee: 0,
    discount: 0,
    totalFee: 0,
    version: "",
    sinceDate: null,
    finishDate: null,
    endProject: false,
};

export const KiiperTaxes = (props) => {
    // Properties
    const { setTexto, setalertType, setShow, companyID, setBackDrop, data, reloadProjects, serviceID } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [taxes, setTaxes] = useState(defaultState);
    const [isError, setIsError] = useState(false);

    /** Component functions */
    useEffect(() => {
        let ogFee = 0;

        if (data.version === "Basic") {
            ogFee = 20;
        }
        if (data.version === "Professional") {
            ogFee = 35;
        }

        setTaxes({
            fee: ogFee,
            discount: data.discount ?? 0,
            totalFee: ogFee * (1 - (data.discount ?? 0) / 100),
            version: data.version ?? "",
            sinceDate: data.since_date
                ? moment(data.since_date).utc()
                : null,
            finishDate: data.end_date ? moment(data.end_date).utc() : null,
            endProject: data.finished_service ?? false,
        });
    }, [data]);

    const handleChangeEndDate = (date) => {
        let endDate = moment(date).endOf("month").startOf("day");

        if (!date) {
            setTaxes({
                ...taxes,
                finishDate: date,
            });
        } else if (taxes.sinceDate) {
            if (endDate.isBefore(moment(taxes.sinceDate))) {
                setTaxes({
                    ...taxes,
                    finishDate: null,
                });
                setTexto(t("reports.endDateMess"));
                setalertType("warning");
                setShow(true);
            } else {
                setTaxes({
                    ...taxes,
                    finishDate: endDate,
                });
            }
        } else {
            setTexto(t("reports.selectStartDate"));
            setalertType("warning");
            setShow(true);
        }
    };

    const handleChangeDiscount = (event) => {
        let discountAux = event.target.value >= 0 && event.target.value <= 100 ? event.target.value : taxes.discount;

        setTaxes({
            ...taxes,
            discount: discountAux,
            totalFee: taxes.fee * (1 - discountAux / 100),
        });
    };

    const updateProject = async () => {
        setBackDrop(true);

        if (taxes.sinceDate && taxes.version) {
            if (taxes.endProject && !taxes.finishDate) {
                setTexto(t("miscellaneous.requiredData"));
                setalertType("warning");
                setShow(true);
                setIsError(true);
                setBackDrop(false);
                return;
            }

            let params = {
                company_id: companyID,
                service_id: serviceID,
                project_id: data.project_id,
                project_company_id: data.project_company_id,
                since_date: moment(taxes.sinceDate).startOf("day").format(),
                end_date: taxes.finishDate ? moment(taxes.finishDate).startOf("day").format() : null,
                discount: taxes.discount ? parseFloat(taxes.discount) : 0,
                fee: taxes.totalFee,
                version: taxes.version,
                finished_service: taxes.endProject,
            };

            await fetch(`/addServicesAndProjects`, {
                method: "POST",
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    "Access-Control-Allow-Origin": "*",
                },
                body: JSON.stringify(params),
            })
                .then((response) => {
                    if (response.ok) {
                        setTexto(t("miscellaneous.successfulUpdate"));
                        setalertType("success");
                        setShow(true);
                        reloadProjects(true, taxes.endProject);
                    } else {
                        setTexto(t("miscellaneous.updatingError"));
                        setalertType("error");
                        setShow(true);
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            setTexto(t("miscellaneous.requiredData"));
            setalertType("warning");
            setShow(true);
            setIsError(true);
        }

        setBackDrop(false);
    };

    const handleChangeSinceDate = (date) => {
        setTaxes({
            ...taxes,
            sinceDate: date,
        });
    };

    const handleChangeVersion = (event) => {
        let newFee = 0;
        switch (event.target.value) {
            case "Basic":
                newFee = 20;
                break;
            default:
                newFee = 35;
                break;
        }

        setTaxes({
            ...taxes,
            version: event.target.value,
            fee: newFee,
            totalFee: newFee * (1 - (taxes.discount ?? 0) / 100),
        });
    };

    const handleChangeSwitch = (event) => {
        setTaxes({
            ...taxes,
            endProject: event.target.checked,
            finishDate: null,
        });
    };

    return (
        <StyledContainer>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={t("language.locale")}
                localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
            >
                <Card elevation={0}>
                    <CardHeader
                        title="Kiiper Taxes subscription"
                        action={
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={taxes.endProject}
                                        inputProps={{
                                            "aria-label": "controlled",
                                        }}
                                        color="secondary"
                                        onChange={handleChangeSwitch}
                                        className={classes.switch}
                                    />
                                }
                                label={
                                    <Typography variant="body1" color="textPrimary">
                                        {t("services.finishProject")}
                                    </Typography>
                                }
                            />
                        }
                    />
                    <Divider />
                    <CardContent>
                        <Grid container alignItems="flex-start" spacing={2}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth required error={isError && taxes.version === ""}>
                                    <InputLabel id="ver-simple-select-label">{t("services.version")}</InputLabel>
                                    <Select
                                        name="version"
                                        labelId="ver-simple-select-label"
                                        IconComponent={KeyboardArrowDownIcon}
                                        value={taxes.version}
                                        label={t("services.version")}
                                        onChange={handleChangeVersion}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    mt: 0.7,
                                                    borderRadius: 2,
                                                    boxShadow: "10px 10px 15px 0px rgba(3,24,81,0.15)",
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value={"Basic"}>Basic</MenuItem>
                                        <MenuItem value={"Professional"}>Professional</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-fee">{t("services.projectFee")}</InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-fee"
                                        value={taxes.fee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.projectFee")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <TextField
                                    fullWidth
                                    label={t("miscellaneous.discount")}
                                    value={taxes.discount}
                                    name="discount"
                                    onChange={handleChangeDiscount}
                                    onBlur={() =>
                                        setTaxes({
                                            ...taxes,
                                            discount: parseFloat(taxes.discount === "" ? 0 : taxes.discount).toFixed(2),
                                        })
                                    }
                                    variant="outlined"
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: 100,
                                        },
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                    sx={{
                                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                            display: "none",
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-totalFee">
                                        {t("proposals.feeTotal")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-totalFee"
                                        value={taxes.totalFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("proposals.feeTotal")}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                <Card elevation={0}>
                    <CardHeader title={t("services.serviceResponsibility")} sx={{ color: "#ff5968" }} />
                    <Divider />
                    <CardContent>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    value={taxes.sinceDate}
                                    onChange={(newValue) => handleChangeSinceDate(newValue)}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required
                                            {...params}
                                            error={isError && taxes.sinceDate === null ? true : false}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month", "day"]}
                                />
                            </Grid>
                            {taxes.endProject && (
                                <Grid item lg={3} sm={6} xs={12}>
                                    <DatePicker
                                        componentsProps={{
                                            actionBar: { actions: ["clear"] },
                                        }}
                                        value={taxes.finishDate}
                                        onChange={(newValue) => handleChangeEndDate(newValue)}
                                        inputFormat={todos.dateFormat}
                                        renderInput={(params) => (
                                            <TextField
                                                fullWidth
                                                required
                                                {...params}
                                                error={isError && !taxes.finishDate}
                                            />
                                        )}
                                        views={["year", "month"]}
                                        inputProps={{ readOnly: true }}
                                        label={t("reports.endDate")}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                    <Divider />
                    <Box className={classes.buttonContainer}>
                        <Button
                            color="secondary"
                            variant="contained"
                            disableElevation
                            classes={{ root: classes.btnRoot }}
                            onClick={updateProject}
                        >
                            {t("miscellaneous.save")}
                        </Button>
                    </Box>
                </Card>
            </LocalizationProvider>
        </StyledContainer>
    );
};
